import mapvalues from "lodash.mapvalues";
import { createRenderer as createFelaRenderer } from "fela";
// fela-preset-web is NOT spread here — it bundles the original (unsafe) extend()
// which has mutation-during-for...in issues. Plugins are listed explicitly so
// safeExtend() and safeCustomPropertyPlugin() replace the unsafe originals.
import embedded from "fela-plugin-embedded";
import unit from "fela-plugin-unit";
import fallbackValue from "fela-plugin-fallback-value";
import prefixer from "fela-plugin-prefixer";
import namedKeys from "fela-plugin-named-keys";
import isPlainObject from "isobject";
import { assignStyle } from "css-in-js-utils";
import { isUndefinedValue } from "fela-utils";

import { breakpointOrder, breakpoints } from "~/styleguide";
import customProperties from "~/styleguide/custom-properties";
import globalStyles from "~/styleguide/global";

// Safe drop-in for fela-plugin-custom-property.
// The original uses `for...in` while calling assignStyle (adds keys) and
// delete (removes keys) on the same object, which makes enumeration order
// implementation-defined. Node.js V8 and browser V8 can produce different
// orders, causing className mismatches on hydration. Snapshotting keys with
// Object.keys() before iterating removes the non-determinism.
function resolveCustomPropertySafe(style, properties) {
    if (!style || typeof style !== "object") return style;
    const keys = Object.keys(style);
    for (let i = 0; i < keys.length; i++) {
        const property = keys[i];
        if (!Object.prototype.hasOwnProperty.call(style, property)) continue;
        const value = style[property];
        if (Object.prototype.hasOwnProperty.call(properties, property)) {
            const resolved = properties[property](value);
            assignStyle(style, resolved);
            if (!Object.prototype.hasOwnProperty.call(resolved, property)) {
                delete style[property];
            }
        }
        if (Object.prototype.hasOwnProperty.call(style, property) && isPlainObject(value)) {
            style[property] = resolveCustomPropertySafe(value, properties);
        }
    }
    return style;
}

function safeCustomPropertyPlugin(properties) {
    return function customPropertyPlugin(style) {
        return resolveCustomPropertySafe(style, properties);
    };
}

// Safe drop-in for fela-plugin-extend.
// Same root cause: objectEach uses `for...in` while assignStyle adds keys
// mid-iteration. Snapshot Object.keys() first to get deterministic ordering.
function safeRemoveUndefined(style) {
    if (!style || typeof style !== "object") return style;
    const keys = Object.keys(style);
    for (const property of keys) {
        const value = style[property];
        if (isPlainObject(value)) {
            style[property] = safeRemoveUndefined(value);
        } else if (Array.isArray(value)) {
            style[property] = value.filter(item => !isUndefinedValue(item));
        } else if (isUndefinedValue(value)) {
            delete style[property];
        }
    }
    return style;
}

function safeExtendStyle(style, extension) {
    if (extension && Object.prototype.hasOwnProperty.call(extension, "condition")) {
        if (extension.condition) {
            assignStyle(style, safeExtendPlugin(extension.style));
        }
    } else {
        assignStyle(style, safeRemoveUndefined(safeExtendPlugin(extension)));
    }
}

function safeExtendPlugin(style) {
    if (!style || typeof style !== "object") return style;
    const keys = Object.keys(style);
    for (const property of keys) {
        if (!Object.prototype.hasOwnProperty.call(style, property)) continue;
        const value = style[property];
        if (property === "extend") {
            const extensions = [].concat(value);
            for (const extension of extensions) {
                safeExtendStyle(style, extension);
            }
            delete style["extend"];
        } else if (isPlainObject(value)) {
            style[property] = safeExtendPlugin(value);
        }
    }
    return style;
}

function safeExtend() {
    return safeExtendPlugin;
}

export const createRenderer = () => {
    const renderer = createFelaRenderer({
        selectorPrefix: "bb_",

        plugins: [
            safeExtend(),
            embedded(),
            unit(),
            fallbackValue(),
            safeCustomPropertyPlugin(customProperties),
            namedKeys({
                ...mapvalues(breakpoints, (width, name) => {
                    const [_, type, dimension] = name.match(
                        /^(min|max)(Width|Height)/
                    );

                    return `@media (${type}-${dimension.toLowerCase()}: ${width}px)`;
                })
            }),
            prefixer()
        ],

        mediaQueryOrder: breakpointOrder.map(name => {
            const type = name.slice(0, 3);

            return `(${type}-width: ${breakpoints[name]}px)`;
        })
    });

    // Sort className strings so server (Node.js V8) and client (browser V8)
    // always produce the same ordering. For...in on mutated objects can give
    // different enumeration sequences between V8 versions. Fela's atomic CSS
    // means each class controls exactly one declaration — sort order is safe.
    const origRenderRule = renderer.renderRule.bind(renderer);
    renderer.renderRule = function(rule, props) {
        const result = origRenderRule(rule, props);
        if (!result) return result;
        const trimmed = result.trim();
        if (!trimmed) return trimmed;
        return trimmed.split(" ").filter(Boolean).sort().join(" ");
    };

    /**
     * Global FELA styles
     */
    renderer.renderStatic(globalStyles, "html,body");

    /**
     * Load static fonts
     */
     renderer.renderFont(
         "Ambit",
         [
             "/fonts/Ambit-Bold.woff2",
             "/fonts/Ambit-Bold.woff"
         ],
         {
             fontWeight: 700,
             fontDisplay: "swap"
         }
     );

     renderer.renderFont(
         "Ambit",
         [
             "/fonts/Ambit-BoldItalic.woff2",
             "/fonts/Ambit-BoldItalic.woff"
         ],
         {
             fontWeight: 700,
             fontStyle: "italic",
             fontDisplay: "swap"
         }
     );

     renderer.renderFont(
         "Ambit",
         [
             "/fonts/Ambit-SemiBold.woff2",
             "/fonts/Ambit-SemiBold.woff"
         ],
         {
             fontWeight: 600,
             fontDisplay: "swap"
         }
     );

     renderer.renderFont(
         "Ambit",
         [
             "/fonts/Ambit-SemiBoldItalic.woff2",
             "/fonts/Ambit-SemiBoldItalic.woff"
         ],
         {
             fontWeight: 600,
             fontStyle: "italic",
             fontDisplay: "swap"
         }
     );

     renderer.renderFont(
         "Ambit",
         [
             "/fonts/Ambit-Regular.woff2",
             "/fonts/Ambit-Regular.woff"
         ],
         {
             fontWeight: 400,
             fontDisplay: "swap"
         }
     );

     renderer.renderFont(
         "Ambit",
         [
             "/fonts/Ambit-Italic.woff2",
             "/fonts/Ambit-Italic.woff"
         ],
         {
             fontWeight: 400,
             fontStyle: "italic",
             fontDisplay: "swap"
         }
     );

    return renderer;
};
